import { useEffect, useState } from 'react';

import { useParams } from 'react-router-dom';
import WebFont from 'webfontloader';

import { getUrlParam } from '../../../utils/url';
import { ExcalidrawSession } from '../components/ExcalidrawSession';
import { WhiteboardProvider } from '../context/WhiteboardContext';
import WhiteboardPdfContainer from '../context/WhiteboardPdfContainer';
import { WhiteboardSessionProvider } from '../context/WhiteboardSessionContext';

// eslint-disable-next-line no-console
console.info(
    process.env.REACT_APP_ENABLE_VERBOSE_LOGGING
        ? 'Verbose logging enabled. You will see websocket messages and state changes logged to the console as they come in.'
        : 'To enable verbose logging in the console, enter `window.enableVerboseLogging = true`'
);

/**
 * View whiteboard session, interact with the canvas, and access whiteboard pages. This is where all the magic happens.
 */
export const WhiteboardView: React.FC = () => {
    const { sessionId } = useParams<{ sessionId: string }>();
    const token = getUrlParam('token', true);
    const [fontLoading, setFontLoading] = useState(true);
    // TODO: Get class id from whiteboard session.
    const classUuid = getUrlParam('class_id', true);

    /**
     * region Apply custom Excalidraw CSS for this page only.
     */
    useEffect(() => {
        // Add `whiteboard` class to allow global styles to target this page.
        document.getElementsByTagName('html')[0].setAttribute('class', 'whiteboard');
        document.body.setAttribute('class', 'whiteboard');

        // For some reason, certain web browsers (e.g. Safari) fail to update the fonts unless they are preloaded.
        // This code ensures the fonts are preloaded before Excalidraw renders any pages.
        WebFont.load({
            loading: () => {
                setFontLoading(false);
            },
            custom: {
                families: ['Cascadia', 'Virgil', 'Lato'],
                urls: [
                    'https://unpkg.com/@excalidraw/excalidraw@0.12.0/dist/excalidraw-assets/Cascadia.woff2',
                    'https://unpkg.com/@excalidraw/excalidraw@0.12.0/dist/excalidraw-assets/Virgil.woff2',
                    'https://fonts.gstatic.com/s/lato/v23/S6u9w4BMUTPHh6UVSwiPGQ3q5d0.woff2',
                    'https://fonts.gstatic.com/s/lato/v23/S6u9w4BMUTPHh7USSwiPGQ3q5d0.woff2',
                    'https://fonts.gstatic.com/s/lato/v23/S6u9w4BMUTPHh50XSwiPGQ3q5d0.woff2'
                ]
            }
        });
    }, [setFontLoading]);

    // The fontLoading variable is used to prevent anything from being rendered until the fonts are available.
    if (fontLoading || !classUuid || !token) {
        return <></>;
    }

    return (
        <WhiteboardProvider classUuid={classUuid} token={token}>
            <WhiteboardSessionProvider sessionId={sessionId}>
                <WhiteboardPdfContainer.Provider>
                    <ExcalidrawSession />
                </WhiteboardPdfContainer.Provider>
            </WhiteboardSessionProvider>
        </WhiteboardProvider>
    );
};
